import { useCallback, useMemo, useState } from 'react';

import { parseInputValue } from 'components/Calculator/utils/parseInputValue';

import type { CalculatorInputField } from 'components/Calculator/config/types';
import type { FormulaScope } from 'components/Calculator/utils/formula';

const toInitialValues = (inputs: CalculatorInputField[]): Record<string, string> =>
  Object.fromEntries(
    inputs.map(({ defaultValue, key }) => [key, defaultValue === undefined ? '' : String(defaultValue)]),
  );

/**
 * A fingerprint of the configured fields. It changes when an editor adds,
 * removes, renames or re-defaults a field in Builder, and not when a reader
 * types, which is exactly when the pre-filled values should be reapplied.
 */
const toFieldSignature = (inputs: CalculatorInputField[]): string =>
  JSON.stringify(inputs.map(({ defaultValue, key }) => [key, defaultValue]));

/**
 * Holds what the reader has typed into one calculator.
 *
 * State is local to the component, deliberately. Several calculators appear in
 * the same post and each has to keep its own figures, so there is no store
 * keyed by route or by anything else shared. That also sidesteps the trap that
 * every Builder page is the same catch-all route, which makes any path-keyed
 * store a single global variable.
 *
 * @param inputs - The input fields as configured in Builder
 * @returns The raw field values, a setter, the parsed scope, and whether a field is still empty
 */
export const useCalculatorInputs = (inputs: CalculatorInputField[]) => {
  const signature = toFieldSignature(inputs);
  const [state, setState] = useState(() => ({ signature, values: toInitialValues(inputs) }));

  // Reset during render rather than in an effect: React re-runs the component
  // immediately with the new state and never commits the stale values, so an
  // editor changing a default in Builder sees it at once with no flash.
  if (state.signature !== signature) {
    setState({ signature, values: toInitialValues(inputs) });
  }

  const setValue = useCallback((key: string, value: string) => {
    setState(current => ({ ...current, values: { ...current.values, [key]: value } }));
  }, []);

  const { scope, waitingForInput } = useMemo(() => {
    const parsed = inputs.map(({ key }) => [key, parseInputValue(state.values[key] ?? '')] as const);

    return {
      scope: Object.fromEntries(parsed.filter(([, value]) => value !== undefined)) as FormulaScope,
      waitingForInput: parsed.some(([, value]) => value === undefined),
    };
  }, [inputs, state.values]);

  return { scope, setValue, values: state.values, waitingForInput };
};
